import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button } from '@mui/material';
import DrawerCDS, { DrawerSize } from '@sharedCR/coreDesignSystem/Drawer/DrawerCDS';
import storage from 'app/services/storage';
import { LOCAL_STORAGE_FORM_KEY } from 'app/shared/components/document/ExtractionField/EditExtractionFields/EditExtractionFieldsForm.constants';
import EditExtractionFieldsFormFieldsPart from 'app/shared/components/document/ExtractionField/EditExtractionFields/EditExtractionFieldsFormFieldsPart';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useDebounce from 'hooks/useDebounce';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import { logger } from 'utils/logger';

import {
  evaluateWorksheetCalculationFieldsAction,
  findWorksheetDocumentAction,
  getDocumentExtractionFilledFieldsAction,
  recalculateDocumentCalculationFieldsAction,
  saveDocumentWorksheetAction,
} from 'store/documents/actions';
import { showErrorMessageAction, showMessageAction } from 'store/global/actions';
import {
  getApplicationChecklistsAction,
  triggerOptionalChecklistRecalculation,
} from 'store/loanApplications/actions';
// Concrete module, not the store/system/actions barrel — the barrel transitively
// loads api modules whose module-scope environment access breaks under Jest.
import { getDocumentFieldTypesAction } from 'store/system/actions/documentFieldTypes.actions';
import { getDocumentExtractionFieldsWithNamedType } from 'store/system/selectors/documentExtractionFields.selectors';

import WorksheetCalculatedValuesPanel from './components/WorksheetCalculatedValuesPanel';
import WorksheetChecklistPanel from './components/WorksheetChecklistPanel';
import WorksheetFieldsLayout from './components/WorksheetFieldsLayout';
import WorksheetLoanFactsPanel from './components/WorksheetLoanFactsPanel';
import WorksheetStatusBar from './components/WorksheetStatusBar';
import { useWorksheetComputedFields } from './hooks/useWorksheetComputedFields';
import { useWorksheetLoanFacts } from './hooks/useWorksheetLoanFacts';
import { useWorksheetPrefill } from './hooks/useWorksheetPrefill';
import {
  WORKSHEET_CALCULATION_DEBOUNCE_MS,
  WORKSHEET_COMPLETE_LABEL,
  WORKSHEET_COMPLETE_SUCCESS_MESSAGE,
  WORKSHEET_DRAWER_DEFAULT_TITLE,
  WORKSHEET_EMPTY_FIELDS_MESSAGE,
  WORKSHEET_FORM_ID,
  WORKSHEET_PRINT_LABEL,
  WORKSHEET_PRINT_POPUP_BLOCKED_MESSAGE,
  WORKSHEET_RECALCULATION_FAILED_MESSAGE,
  WORKSHEET_SAVE_DRAFT_LABEL,
  WORKSHEET_SAVE_SUCCESS_MESSAGE,
} from './WorksheetDrawer.constants';
import {
  getWorksheetBodyStyles,
  worksheetFormStyles,
  worksheetLoanFactsColumnStyles,
} from './WorksheetDrawer.styles';
import { WorksheetDrawerTestIds } from './WorksheetDrawer.testIds';
import {
  buildWorksheetCalculationValues,
  buildWorksheetInitialForm,
  buildWorksheetPrintAnswers,
  buildWorksheetPrintDocument,
  buildWorksheetSavePayload,
  normalizeWorksheetInitialForm,
} from './WorksheetDrawer.utils';

const WorksheetDrawer = ({
  open,
  onClose,
  documentTypeId,
  applicationId,
  loanId = null,
  worksheetDocumentId = null,
  checklistId = null,
  title = WORKSHEET_DRAWER_DEFAULT_TITLE,
}) => {
  const dispatch = useDispatch();

  const [duplicatedFields, setDuplicatedFields] = useState([]);
  const [documentExtractionFieldsTree, setDocumentExtractionFieldsTree] = useState([]);
  // Launchers mount the drawer when it opens, so it starts loading: otherwise its first frame, before the
  // open effect kicks off the load, reads "Not saved yet" for an audit that was saved.
  const [isLoading, setIsLoading] = useState(true);
  const [savedDocumentId, setSavedDocumentId] = useState(worksheetDocumentId);
  const [worksheetFilledFields, setWorksheetFilledFields] = useState(null);
  const [calculatedValues, setCalculatedValues] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // True once this open is known to start a worksheet the application does not have yet (no saved
  // document to reopen) — only then may fields be prefilled.
  const [isNewWorksheet, setIsNewWorksheet] = useState(false);
  // True once a reopened worksheet's saved values are in the form — only then may they be compared
  // with the loan's.
  const [isSavedWorksheetLoaded, setIsSavedWorksheetLoaded] = useState(false);
  // Worksheet types without calculation fields answer with an empty list — stop asking after that.
  // Gates the live preview and the snapshot printed on Save; Save always recalculates the stored values.
  const hasCalculationFieldsRef = useRef(true);
  // Debouncing spaces out evaluate requests but not their responses: only the latest one applies.
  const latestEvaluationRef = useRef(0);
  // Set when an existing worksheet's values are loaded: the first form they populate becomes the
  // clean baseline, so closing an unedited worksheet does not ask to discard changes.
  const isBaselinePendingRef = useRef(false);

  const documentExtractionFields = useSelector(getDocumentExtractionFieldsWithNamedType);
  const { sections: loanFactsSections, loanFacts } = useWorksheetLoanFacts({
    applicationId,
    isOpen: open,
  });
  // Recomputed once the type's field definitions load, so Bool values arrive as real booleans.
  const normalizedWorksheetInitialForm = useMemo(
    () =>
      worksheetFilledFields
        ? normalizeWorksheetInitialForm(
            buildWorksheetInitialForm(worksheetFilledFields, documentExtractionFields),
            documentExtractionFields,
          )
        : null,
    [worksheetFilledFields, documentExtractionFields],
  );

  const { initialForm, ...formProps } = useForm({});
  const { form, validateForm, setForm, setInitialFormValues, isFormChanged } = formProps;
  const prefillHints = useWorksheetPrefill({
    loanId,
    applicationId,
    isOpen: open,
    isNewWorksheet,
    isSavedWorksheetLoaded,
    fields: documentExtractionFieldsTree,
    form,
    setInForm: formProps.setInForm,
    loanFacts,
  });
  const computedHints = useWorksheetComputedFields({
    fields: documentExtractionFieldsTree,
    form,
    calculatedValues,
    setInForm: formProps.setInForm,
  });
  const fieldHints = useMemo(
    () => ({ ...prefillHints, ...computedHints }),
    [prefillHints, computedHints],
  );

  const onSave = useCallback(
    async completed => {
      if (!(await validateForm({ noFormChangedValidate: true }))) {
        return;
      }

      const payload = buildWorksheetSavePayload({
        documentTypeId,
        applicationId,
        loanId,
        completed,
        form,
        documentExtractionFieldsTree,
      });

      if (!payload.values?.length && !payload.tables?.length) {
        dispatch(
          showMessageAction({
            message: WORKSHEET_EMPTY_FIELDS_MESSAGE,
            variant: 'error',
          }),
        );

        return;
      }

      try {
        setIsLoading(true);

        // The saved PDF prints the calculated values as of this save: evaluate them now rather than
        // reuse the debounced preview, which can lag the latest edit (types without calculation
        // fields skip it). A failed evaluation must not block the save — it saves without them.
        const calculatedValues = hasCalculationFieldsRef.current
          ? await dispatch(
              evaluateWorksheetCalculationFieldsAction({
                documentTypeId,
                values: buildWorksheetCalculationValues({ form, documentExtractionFieldsTree }),
                silent: true,
              }),
            ).then(
              results => (results ?? []).map(({ name, value }) => ({ name, value })),
              evaluationErr => {
                logger.error(evaluationErr);

                return null;
              },
            )
          : null;

        const savedDocument = await dispatch(
          saveDocumentWorksheetAction(
            calculatedValues ? { ...payload, calculatedValues } : payload,
          ),
        );
        const documentId = savedDocument?.id ?? savedDocument?.documentId ?? savedDocumentId;

        setSavedDocumentId(documentId);
        setLastSavedAt(new Date().toISOString());
        // The saved values are the new baseline — closing after a Save must not warn about
        // discarding changes.
        setInitialFormValues(form);

        if (documentId) {
          // Stores the calculated values where checklists / loan actions read them, so it runs
          // before the checklist refresh below. A failure must not undo the (successful) save.
          const storedCalculatedValues = await dispatch(
            recalculateDocumentCalculationFieldsAction({
              documentId,
              silent: true,
            }),
          ).catch(recalculationErr => {
            logger.error(recalculationErr);
            dispatch(
              showMessageAction({
                message: WORKSHEET_RECALCULATION_FAILED_MESSAGE,
                variant: 'warning',
              }),
            );

            return [];
          });

          if (applicationId && storedCalculatedValues?.length) {
            // Stored results only reach checklists on their next execution — request one now
            // rather than racing the Documents save event that ran before the values existed.
            // Best effort: a subtree-scoped broker is not allowed to trigger it (403).
            try {
              await dispatch(
                triggerOptionalChecklistRecalculation({
                  applicationId,
                  silent: true,
                }),
              );
            } catch (syncErr) {
              logger.error(syncErr);
            }
          }
        }

        if (applicationId) {
          // The checklist refresh is auxiliary display data; a failure must not
          // suppress the save success toast or keep the drawer open on Complete.
          try {
            await dispatch(
              getApplicationChecklistsAction({
                applicationId,
                includePendingChanges: true,
              }),
            );
          } catch (checklistErr) {
            logger.error(checklistErr);
          }
        }

        dispatch(
          showMessageAction({
            message: completed
              ? WORKSHEET_COMPLETE_SUCCESS_MESSAGE
              : WORKSHEET_SAVE_SUCCESS_MESSAGE,
            variant: 'success',
          }),
        );

        if (completed) {
          onClose();
        }
      } catch (err) {
        logger.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [
      validateForm,
      documentTypeId,
      applicationId,
      loanId,
      form,
      documentExtractionFieldsTree,
      dispatch,
      savedDocumentId,
      setInitialFormValues,
      onClose,
    ],
  );

  const onSaveDraft = useCallback(() => onSave(false), [onSave]);
  const onPrint = useCallback(() => {
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      dispatch(
        showErrorMessageAction({
          message: WORKSHEET_PRINT_POPUP_BLOCKED_MESSAGE,
        }),
      );

      return;
    }

    printWindow.document.write(
      buildWorksheetPrintDocument({
        title,
        printedAt: new Date().toLocaleString(),
        loanFactsSections,
        answerSections: buildWorksheetPrintAnswers({
          form,
          documentExtractionFieldsTree,
        }),
        calculatedValues,
      }),
    );
    printWindow.document.close();
  }, [dispatch, title, loanFactsSections, form, documentExtractionFieldsTree, calculatedValues]);
  const onComplete = useCallback(() => onSave(true), [onSave]);

  useEffectSafeStrictMode(() => {
    if (!open) {
      return;
    }

    // Start each open from a clean slate so a prior document's in-progress edits
    // (persisted to local storage by the reused extraction machinery) never bleed in.
    storage.removeItem(LOCAL_STORAGE_FORM_KEY);
    setSavedDocumentId(worksheetDocumentId);
    setLastSavedAt(null);
    setIsNewWorksheet(false);
    setIsSavedWorksheetLoaded(false);
    setForm({});
    setDuplicatedFields([]);
    setCalculatedValues([]);
    hasCalculationFieldsRef.current = true;
    latestEvaluationRef.current += 1;

    // The extraction-field tree joins field DEFINITIONS with field TYPES; nothing
    // guarantees a launch surface has already loaded the types slice (the loan-editor
    // Documents tab happens to, other launchers may not), so fetch it on open or the
    // form renders empty there.
    dispatch(getDocumentFieldTypesAction()).catch(Error);

    if (applicationId) {
      dispatch(
        getApplicationChecklistsAction({
          applicationId,
          includePendingChanges: true,
        }),
      ).catch(Error);
    }

    const loadWorksheetValues = documentId =>
      dispatch(getDocumentExtractionFilledFieldsAction({ documentId, silent: true })).then(
        filledFields => {
          isBaselinePendingRef.current = true;
          setWorksheetFilledFields(filledFields ?? []);
        },
      );

    // The form stays disabled until the saved values land, so nothing typed meanwhile is overwritten.
    const withLoading = promise => {
      setIsLoading(true);

      return promise.catch(Error).finally(() => setIsLoading(false));
    };

    if (worksheetDocumentId) {
      withLoading(loadWorksheetValues(worksheetDocumentId));

      return;
    }

    setWorksheetFilledFields(null);

    if (!applicationId) {
      setIsNewWorksheet(true);
      setIsLoading(false);

      return;
    }

    // Launched without a document (e.g. from a loan action): the Documents service keeps a single
    // worksheet per type + application, so reopen that one instead of starting from a blank form.
    withLoading(
      dispatch(
        findWorksheetDocumentAction({
          documentTypeId,
          applicationId,
          silent: true,
        }),
      ).then(existingDocument => {
        if (!existingDocument?.id) {
          setIsNewWorksheet(true);

          return null;
        }

        setSavedDocumentId(existingDocument.id);
        setLastSavedAt(
          existingDocument.lifeTimeInfo?.modifiedDate ??
            existingDocument.lifeTimeInfo?.createdDate ??
            null,
        );

        return loadWorksheetValues(existingDocument.id);
      }),
    );
  }, [open, worksheetDocumentId, documentTypeId, applicationId, dispatch, setForm]);

  useEffectSafeStrictMode(() => {
    if (!isBaselinePendingRef.current) {
      return;
    }

    // Only the values of fields the type still defines reach the form (results of deleted fields
    // are dropped), so compare on those. Wait until the form reflects them — the fields part
    // first fills in defaults.
    const loadedValues = _.pick(
      normalizedWorksheetInitialForm,
      documentExtractionFields.map(({ id }) => id),
    );

    if (_.isEmpty(loadedValues) || !_.isMatch(form, loadedValues)) {
      return;
    }

    isBaselinePendingRef.current = false;
    setInitialFormValues(form);
    setIsSavedWorksheetLoaded(true);
  }, [form, normalizedWorksheetInitialForm, documentExtractionFields, setInitialFormValues]);

  const evaluateCalculatedValues = useDebounce(async ({ typeId, values }) => {
    latestEvaluationRef.current += 1;
    const evaluationId = latestEvaluationRef.current;

    try {
      const results = await dispatch(
        evaluateWorksheetCalculationFieldsAction({
          documentTypeId: typeId,
          values,
          silent: true,
        }),
      );

      if (evaluationId !== latestEvaluationRef.current) {
        return;
      }

      hasCalculationFieldsRef.current = !!results?.length;
      setCalculatedValues(results ?? []);
    } catch (err) {
      logger.error(err);
    }
  }, WORKSHEET_CALCULATION_DEBOUNCE_MS);

  useEffectSafeStrictMode(() => {
    if (!open || !documentExtractionFieldsTree.length || !hasCalculationFieldsRef.current) {
      return;
    }

    evaluateCalculatedValues({
      typeId: documentTypeId,
      values: buildWorksheetCalculationValues({ form, documentExtractionFieldsTree }),
    });
  }, [open, form, documentExtractionFieldsTree, documentTypeId, evaluateCalculatedValues]);

  const renderWorksheetFields = useCallback(
    ({
      fields,
      disabled,
      errors,
      form: fieldsForm,
      setInForm,
      validateField,
      onChange,
      onAdd,
      onRemove,
    }) => (
      <WorksheetFieldsLayout
        disabled={disabled}
        errors={errors}
        fieldHints={fieldHints}
        fields={fields}
        form={fieldsForm}
        setInForm={setInForm}
        validateField={validateField}
        onAdd={onAdd}
        onChange={onChange}
        onRemove={onRemove}
      />
    ),
    [fieldHints],
  );

  const content = (
    <Box data-testid={WorksheetDrawerTestIds.FORM} id={WORKSHEET_FORM_ID} sx={worksheetFormStyles}>
      <WorksheetStatusBar
        calculatedValues={calculatedValues}
        checklistId={checklistId}
        isLoading={isLoading}
        lastSavedAt={lastSavedAt}
      />

      <Box
        sx={getWorksheetBodyStyles({
          hasLoanFacts: !!loanFactsSections.length,
        })}
      >
        <Box sx={worksheetLoanFactsColumnStyles}>
          <WorksheetLoanFactsPanel sections={loanFactsSections} />
        </Box>

        <Box>
          <EditExtractionFieldsFormFieldsPart
            isSkipDataLoading
            skipBooleanDefaults
            docId={savedDocumentId ?? ''}
            documentExtractionFieldsTree={documentExtractionFieldsTree}
            documentTypeId={documentTypeId}
            duplicatedFields={duplicatedFields}
            initialForm={normalizedWorksheetInitialForm}
            isLoading={isLoading}
            isView={false}
            renderFields={renderWorksheetFields}
            setDocumentExtractionFieldsTree={setDocumentExtractionFieldsTree}
            setDuplicatedFields={setDuplicatedFields}
            setIsLoading={setIsLoading}
            {...formProps}
          />

          <WorksheetCalculatedValuesPanel calculatedValues={calculatedValues} />

          <WorksheetChecklistPanel checklistId={checklistId} isLoading={isLoading} />
        </Box>
      </Box>
    </Box>
  );

  const actions = (
    <>
      <Button
        data-testid={WorksheetDrawerTestIds.PRINT_BUTTON}
        disabled={isLoading}
        type="button"
        variant="text"
        onClick={onPrint}
      >
        {WORKSHEET_PRINT_LABEL}
      </Button>
      <Button
        data-testid={WorksheetDrawerTestIds.SAVE_DRAFT_BUTTON}
        disabled={isLoading}
        type="button"
        variant="outlined"
        onClick={onSaveDraft}
      >
        {WORKSHEET_SAVE_DRAFT_LABEL}
      </Button>
      <Button
        data-testid={WorksheetDrawerTestIds.COMPLETE_BUTTON}
        disabled={isLoading}
        type="button"
        variant="contained"
        onClick={onComplete}
      >
        {WORKSHEET_COMPLETE_LABEL}
      </Button>
    </>
  );

  return (
    <DrawerCDS
      actions={actions}
      content={content}
      dataTestId={WorksheetDrawerTestIds.DRAWER}
      isDirty={isFormChanged}
      isLoading={isLoading}
      open={open}
      size={DrawerSize.STANDARD}
      title={title}
      onClose={onClose}
    />
  );
};

WorksheetDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  documentTypeId: PropTypes.string.isRequired,
  applicationId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  loanId: PropTypes.string,
  worksheetDocumentId: PropTypes.string,
  checklistId: PropTypes.string,
  title: PropTypes.string,
};

export default WorksheetDrawer;
